import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import HeroField from "@/components/HeroField";
import DeviceMockup from "@/components/DeviceMockup";
import CaseCard from "@/components/CaseCard";
import { MaskedLine, FadeIn, Reveal, Eyebrow } from "@/components/Motion";
import { SERVICES, MANIFESTO, SLOGAN, FALLBACK_CASE_STUDIES, FALLBACK_TESTIMONIALS, EDUANANT_URL } from "@/data/site";
import { EDUANANT } from "@/data/eduanant";
import { SNAPXOPS } from "@/data/products";
import { fetchCaseStudies, fetchTestimonials } from "@/lib/api";
import { INTRO_PENDING, HERO_BASE_DELAY } from "@/lib/intro";
import { ServiceMotif } from "@/components/ServiceMotif";
import ClientLogoBanner from "@/components/ClientLogoBanner";
import SEOHead from "@/components/SEOHead";
import ScrollToTop from "@/components/ScrollToTop";
import { absoluteUrl } from "@/lib/seo";

const D = INTRO_PENDING ? HERO_BASE_DELAY : 0;

const Hero = () => (
  <section className="noise relative flex min-h-screen flex-col justify-center overflow-hidden md:justify-end" data-testid="home-hero">
    <HeroField />
    <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-40 md:px-12 md:pb-24">
      <FadeIn delay={D + 0.2}>
        <a
          href={EDUANANT_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-eduanant-pill"
          className="group mb-10 inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/[0.03] py-2 pl-3 pr-5 text-xs tracking-wide text-white/70 backdrop-blur-md transition-colors duration-300 hover:border-volt/50 hover:text-white"
        >
          <span className="rounded-full bg-volt px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-ink">New</span>
          EduAnant — our school operating system, now live in production
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </FadeIn>
      <FadeIn delay={D + 0.28}>
        <p className="font-serif-display mb-8 text-lg italic text-volt/70 md:text-xl" data-testid="hero-slogan">{SLOGAN}</p>
      </FadeIn>
      <h1 className="max-w-5xl text-4xl font-black leading-[1.06] tracking-tighter min-[400px]:text-5xl sm:text-6xl md:text-7xl lg:text-8xl" data-testid="hero-headline">
        <MaskedLine delay={D + 0.35}>We build <span className="font-serif-display font-light italic text-volt">products.</span></MaskedLine>
        <MaskedLine delay={D + 0.5}>We build <span className="font-serif-display font-light italic">brands.</span></MaskedLine>
        <MaskedLine delay={D + 0.65}>We build <span className="font-serif-display font-light italic">businesses.</span></MaskedLine>
      </h1>
      <FadeIn delay={D + 1} className="mt-10 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
        <p className="max-w-xl text-base leading-relaxed text-white/55 md:text-lg" data-testid="hero-subcopy">
          Snapx is a product + services company. Our flagship, EduAnant, runs a school of {EDUANANT.liveSchool.students} students every day — and we bring the same production-grade craft to your web, mobile, brand and growth.
        </p>
        <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
          <Link
            to="/products/eduanant"
            data-testid="hero-explore-eduanant-button"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-volt px-7 py-3.5 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105"
          >
            Explore EduAnant
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-start-project-button"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:border-white/60"
          >
            Start a project
          </Link>
        </div>
      </FadeIn>
    </div>
  </section>
);

const EduStrip = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  return (
    <section ref={ref} className="relative mx-auto max-w-7xl overflow-hidden px-6 py-20 md:px-12 md:py-48" data-testid="eduanant-strip">
      <div className="grid items-center gap-16 md:grid-cols-2">
        <div>
          <Reveal><Eyebrow>01 — Products · Flagship</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 text-4xl font-black tracking-tighter sm:text-5xl lg:text-6xl">
              EduAnant<span className="text-volt">,</span><br />
              <span className="font-serif-display font-light italic text-white/80">India's school OS.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-white/55">
              Admissions, fees, attendance, exams, HR, library — {EDUANANT.modules} modules in one calm system, with teacher and parent portals and an Android app rated {EDUANANT.playStore.rating}★ on Google Play. From ₹{EDUANANT.pricing.annualRate} per student a month. Not a case study of someone else's product. Ours.
            </p>
          </Reveal>
          <Reveal delay={0.3} className="mt-10 flex flex-wrap gap-4">
            <a
              href={EDUANANT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="edustrip-visit-button"
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105"
            >
              Visit eduanant.cloud <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link to="/products/eduanant" data-testid="edustrip-deepdive-link" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-bold text-white transition-colors hover:border-white/60">
              Product deep-dive
            </Link>
          </Reveal>
        </div>
        <motion.div style={{ y }} className="will-change-transform">
          <DeviceMockup />
        </motion.div>
      </div>
    </section>
  );
};

/** Our second product, in early access — a compact band rather than a second flagship. */
const SnapxOpsStrip = () => (
  <section className="mx-auto max-w-7xl px-6 pb-20 md:px-12 md:pb-32" data-testid="snapxops-strip">
    <Reveal>
      <Link
        to="/products/snapxops"
        data-testid="home-snapxops-link"
        className="group flex flex-col gap-8 rounded-3xl border border-amber-400/20 bg-amber-400/[0.03] p-8 transition-colors duration-500 hover:border-amber-400/45 hover:bg-amber-400/[0.06] md:flex-row md:items-center md:justify-between md:p-12"
      >
        <div className="max-w-2xl">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/45">Also from Snapx</p>
            <span className="rounded-full border border-amber-400/30 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">{SNAPXOPS.status}</span>
          </div>
          <h2 className="mt-5 text-3xl font-black tracking-tighter sm:text-4xl">
            SNAPXOps<span className="text-amber-400">.</span>{" "}
            <span className="font-serif-display font-light italic text-white/70">Every job, every route card, live.</span>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-white/55 md:text-base">
            Job and route-card tracking for {SNAPXOPS.segment}. It replaces the PPC workbook and the phone calls with a live
            board every department updates itself.
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-2 text-sm font-bold text-amber-300 transition-colors group-hover:text-amber-200">
          Explore SNAPXOps
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </Link>
    </Reveal>
  </section>
);

const ServiceStripCard = ({ service }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={`/services/${service.slug}`}
      data-testid={`home-service-card-${service.slug}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`group flex min-h-[560px] w-[85vw] shrink-0 flex-col justify-between rounded-3xl border bg-white/[0.02] p-8 transition-[border-color,background-color,box-shadow] duration-500 hover:bg-white/[0.05] sm:w-[420px] ${hovered ? "border-white/25" : "border-white/10"}`}
      style={{ "--card-accent": service.accent, boxShadow: hovered ? `0 24px 60px -24px ${service.accent}33` : "none" }}
    >
      <div className="flex items-start justify-between">
        <span className="font-serif-display text-6xl font-light italic text-white/15 transition-colors duration-500 group-hover:text-[color:var(--card-accent)]">
          {service.index}
        </span>
        <ArrowUpRight className="h-6 w-6 text-white/30 transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[color:var(--card-accent)]" />
      </div>
      <div className="pointer-events-none my-6 overflow-hidden rounded-2xl transition-transform duration-500 group-hover:scale-[1.03]" aria-hidden="true">
        <ServiceMotif slug={service.slug} accent={service.accent} compact active={hovered} />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em]" style={{ color: service.accent }}>
          {service.tier === "technology" ? "Technology" : "Growth"}
        </p>
        <h3 className="mt-3 text-3xl font-black tracking-tight text-white">{service.name}</h3>
        <p className="mt-4 text-sm leading-relaxed text-white/50">{service.tagline}</p>
      </div>
    </Link>
  );
};

const ServicesHorizontal = () => {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [range, setRange] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (!trackRef.current) return;
      setRange(Math.max(0, trackRef.current.scrollWidth - window.innerWidth + 96));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const { scrollYProgress } = useScroll({ target: sectionRef });
  const x = useTransform(scrollYProgress, [0, 1], [0, -range]);

  return (
    <section className="relative hidden md:block" ref={sectionRef} style={{ height: "380vh" }} data-testid="services-horizontal-section">
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div className="mx-auto mb-10 w-full max-w-7xl px-12">
          <Eyebrow>02 — Services</Eyebrow>
          <div className="flex items-end justify-between gap-8">
            <h2 className="mt-6 text-4xl font-black tracking-tighter sm:text-5xl">
              Twelve disciplines. <span className="font-serif-display font-light italic text-white/70">One team.</span>
            </h2>
            <p className="mb-2 hidden shrink-0 text-xs uppercase tracking-[0.25em] text-white/50 lg:block">Scroll to travel →</p>
          </div>
          <div className="mt-8 h-px w-full bg-white/10">
            <motion.div className="h-px origin-left bg-volt" style={{ scaleX: scrollYProgress }} />
          </div>
        </div>
        <motion.div ref={trackRef} style={{ x }} className="flex gap-6 pl-12 pr-24 will-change-transform">
          {SERVICES.map((s) => (
            <ServiceStripCard key={s.slug} service={s} />
          ))}
          <div className="flex w-[300px] shrink-0 items-center">
            <Link to="/services" data-testid="home-services-all-link" className="group inline-flex items-center gap-3 text-lg font-bold text-white/60 transition-colors hover:text-volt">
              All services <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/**
 * Mobile services carousel.
 *
 * The desktop version is a scroll-hijacked horizontal track, which does not work on
 * touch. This is a native CSS scroll-snap carousel — same rich cards, same motifs, no
 * JavaScript, and it behaves the way a phone user expects a swipeable row to behave.
 */
const ServicesMobile = () => (
  <section className="py-20 md:hidden" data-testid="services-mobile-section">
    <div className="mx-auto max-w-7xl px-6">
      <Eyebrow>02 — Services</Eyebrow>
      <h2 className="mt-6 text-3xl font-black tracking-tighter min-[400px]:text-4xl">
        Twelve disciplines. <span className="font-serif-display font-light italic text-white/70">One team.</span>
      </h2>
      <p className="mt-4 text-xs uppercase tracking-[0.25em] text-white/50">Swipe to explore →</p>
    </div>

    <div className="mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {SERVICES.map((s) => (
        <Link
          key={s.slug}
          to={`/services/${s.slug}`}
          data-testid={`home-service-card-mobile-${s.slug}`}
          className="flex w-[78vw] max-w-[320px] shrink-0 snap-start flex-col justify-between rounded-3xl border border-white/10 bg-white/[0.02] p-6"
          style={{ "--card-accent": s.accent }}
        >
          <div className="flex items-start justify-between">
            <span className="font-serif-display text-4xl font-light italic text-[color:var(--card-accent)]">{s.index}</span>
            <ArrowUpRight className="h-5 w-5 text-white/35" />
          </div>
          <div className="pointer-events-none my-5 overflow-hidden rounded-2xl" aria-hidden="true">
            <ServiceMotif slug={s.slug} accent={s.accent} compact />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: s.accent }}>
              {s.tier === "technology" ? "Technology" : "Growth"}
            </p>
            <h3 className="mt-2 text-xl font-black tracking-tight text-white">{s.name}</h3>
            <p className="mt-3 text-sm leading-relaxed text-white/50">{s.tagline}</p>
          </div>
        </Link>
      ))}
      <Link
        to="/services"
        data-testid="home-services-all-link-mobile"
        className="flex w-[60vw] shrink-0 snap-start items-center gap-3 pl-2 text-base font-bold text-white/60"
      >
        All services <ArrowRight className="h-5 w-5" />
      </Link>
    </div>
  </section>
);

const Manifesto = () => (
  <section className="border-t border-white/10" data-testid="manifesto-section">
    <div className="mx-auto max-w-7xl px-6 py-32 md:px-12 md:py-48">
      <Reveal><Eyebrow>03 — Manifesto</Eyebrow></Reveal>
      <div className="mt-16 space-y-24 md:space-y-32">
        {MANIFESTO.map((chapter, i) => (
          <div key={chapter.number} className="grid gap-8 md:grid-cols-12">
            <Reveal className="md:col-span-3">
              <span className="font-serif-display text-7xl font-light italic text-stroke-volt md:text-8xl">{chapter.number}</span>
            </Reveal>
            <div className="md:col-span-9">
              <Reveal delay={0.1}>
                <h3 className="text-3xl font-black tracking-tight sm:text-4xl md:text-5xl">{chapter.title}</h3>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/55 md:text-lg">{chapter.body}</p>
              </Reveal>
              {i < MANIFESTO.length - 1 && <div className="mt-16 h-px w-full bg-white/10 md:mt-24" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WorkPreview = () => {
  const [studies, setStudies] = useState(FALLBACK_CASE_STUDIES);
  useEffect(() => {
    fetchCaseStudies().then((d) => d.length && setStudies(d)).catch(() => {});
  }, []);
  return (
    <section className="border-t border-white/10" data-testid="work-preview-section">
      <div className="mx-auto max-w-7xl px-6 py-32 md:px-12 md:py-48">
        <div className="flex items-end justify-between">
          <div>
            <Reveal><Eyebrow>04 — Selected work</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 text-4xl font-black tracking-tighter sm:text-5xl">
                Proof, <span className="font-serif-display font-light italic text-white/70">not promises.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/50 md:text-base">
                Every client here is still with us — we stay on long after launch.
              </p>
            </Reveal>
          </div>
          <Link to="/work" data-testid="home-work-all-link" className="group hidden items-center gap-2 text-sm font-bold text-white/60 transition-colors hover:text-volt md:inline-flex">
            All work <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="mt-16 grid gap-10 md:grid-cols-2 md:gap-12">
          {studies.slice(0, 4).map((s, i) => (
            <CaseCard key={s.slug} study={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const [items, setItems] = useState(FALLBACK_TESTIMONIALS);
  useEffect(() => {
    fetchTestimonials().then((d) => d.length && setItems(d)).catch(() => {});
  }, []);
  return (
    <section className="border-t border-white/10" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-6 py-32 md:px-12 md:py-48">
        <Reveal><Eyebrow>05 — Word of mouth</Eyebrow></Reveal>
        <div className="mt-16 grid gap-6 md:grid-cols-2">
          {items.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <figure className="flex h-full flex-col justify-between rounded-3xl border border-white/10 bg-white/[0.02] p-8 md:p-10" data-testid={`testimonial-${i}`}>
                <blockquote className="font-serif-display text-xl font-light leading-relaxed text-white/85 md:text-2xl">
                  “{t.quote}”
                </blockquote>
                <figcaption className="mt-8">
                  <p className="text-sm font-bold text-white">{t.name}</p>
                  <p className="mt-1 text-xs text-white/45">{t.role}, {t.company}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const HomeCta = () => (
  <section className="relative overflow-hidden border-t border-white/10" data-testid="home-cta-section">
    <div className="pointer-events-none absolute left-1/2 top-1/2 h-[500px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-volt/[0.06] blur-3xl" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-6 py-32 text-left md:px-12 md:py-48">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.25em] text-white/50">Next step</p>
      </Reveal>
      <Reveal delay={0.1}>
        <Link to="/contact" className="group mt-6 block" data-testid="home-cta-link">
          <span className="text-5xl font-black tracking-tighter transition-colors duration-500 group-hover:text-volt sm:text-7xl lg:text-8xl">
            Let's build something
          </span>
          <span className="font-serif-display mt-2 flex items-center gap-6 text-5xl font-light italic text-white/70 sm:text-7xl lg:text-8xl">
            that ships.
            <ArrowRight className="h-12 w-12 text-volt transition-transform duration-500 group-hover:translate-x-4 md:h-20 md:w-20" />
          </span>
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      url: absoluteUrl("/"),
      name: "Snapx Technologies — We build products, brands, businesses",
      description:
        "Snapx Technologies is a product + services company from Patna, India. Creators of EduAnant, India's school operating system.",
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Services offered by Snapx Technologies",
      itemListElement: SERVICES.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: s.name,
        url: absoluteUrl(`/services/${s.slug}`),
      })),
    },
  ];
  return (
    <div data-testid="home-page">
      <SEOHead
        description="Snapx Technologies is a product + services company from Patna, India. Creators of EduAnant, India's school operating system — plus web, mobile, branding, motion, marketing and finance services."
        path="/"
        jsonLd={schema}
      />
      <Hero />
      <ClientLogoBanner />
      <EduStrip />
      <SnapxOpsStrip />
      <ServicesHorizontal />
      <ServicesMobile />
      <Manifesto />
      <WorkPreview />
      <Testimonials />
      <HomeCta />
      <ScrollToTop />
    </div>
  );
}
